/**
 * GanttChart — virtualised, performant Gantt for 5k+ rows.
 *
 * Architecture overview (iter10 rewrite):
 *  - One horizontally + vertically scrolling list container.
 *  - Header is `position:sticky; top:0` and stays pinned during vertical scroll
 *    (via react-window's `innerElementType`).
 *  - Left-side column cells are `position:sticky; left:0` so they stay pinned
 *    during horizontal scroll.
 *  - Per-column filters: text input for the searchable "Tarefa" / "Nome"
 *    column, dropdown <select> for every other column (auto-populated from
 *    distinct values in the dataset).
 *  - Resizable columns: drag the right edge of each column header.
 *  - Resizable split between table and timeline: drag the vertical handle
 *    between the two.
 *  - Collapsible summary groups: click the ▼/▶ chevron on a summary row.
 *  - PNG export: see `renderFullGanttForExport` — renders the FULL filtered
 *    dataset (no virtualisation) into a hidden DOM node so html2canvas can
 *    capture EVERYTHING, not just the viewport.
 */
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import GanttStickyHeader, { HEADER_HEIGHT } from "./GanttStickyHeader";
import { useI18n } from "../context/I18nContext";

const ROW_HEIGHT = 28;
const MIN_COL_WIDTH = 40;
const MAX_COL_WIDTH = 600;
const MIN_SPLIT = 200;
const TASK_SEARCHABLE_KEYS = new Set(["name", "task_id", "wbs"]);

// Zoom presets — `labelKey` is resolved via `t(labelKey)` at render time so
// language switching works without re-creating components. `colWidth=null`
// means "ajust to container width".
const ZOOM_PRESETS = {
  "7d":  { labelKey: "zoom_7d",  days: 7,   colWidth: 100 },
  "15d": { labelKey: "zoom_15d", days: 15,  colWidth: 60 },
  "30d": { labelKey: "zoom_30d", days: 30,  colWidth: 32 },
  "1y":  { labelKey: "zoom_1y",  days: 365, colWidth: 6  },
  "fit": { labelKey: "zoom_fit", days: null, colWidth: null },
};

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function diffDays(a, b) { return Math.round((a - b) / 86400000); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function getValue(task, key) {
  if (!key) return undefined;
  if (key.startsWith("custom_fields.")) {
    return (task.custom_fields || {})[key.slice("custom_fields.".length)];
  }
  return task[key];
}

function formatCell(value, type) {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (type === "date") return String(value).slice(0, 10);
  if (type === "percent") return `${Math.round(value)}%`;
  if (type === "number") return typeof value === "number" ? value.toLocaleString() : String(value);
  if (type === "boolean") return value ? "✓" : "—";
  return String(value);
}

/**
 * Build a map of `column.key -> Array<string>` with the unique non-empty
 * values for each column.
 *
 * Crucially: when computing the dropdown values for column X, we exclude
 * column X's own filter from the in-effect set so the dropdown keeps
 * showing all alternatives the user could switch to. Filters of OTHER
 * columns DO cascade, so users only see options that wouldn't immediately
 * yield zero rows.
 */
function buildDistinctValues(tasks, columns, collapsed) {
  const visible = columns.filter((c) => c.visible);

  // Pre-compute the candidate pool (already collapse-filtered) so each
  // column only re-runs the per-column filter loop.
  const collapsePool = filterByCollapse(tasks, collapsed);

  // Same active-filter heuristic as the main pipeline — supports both legacy
  // string filters and iter40 array filters.
  const isActiveFilter = (f) => Array.isArray(f) ? f.length > 0 : !!(f && f.toString().trim());

  const out = {};
  for (const c of visible) {
    if (TASK_SEARCHABLE_KEYS.has(c.key)) continue; // searchable text, no dropdown

    // Apply all OTHER columns' filters first
    const others = visible.filter((x) => x.key !== c.key && isActiveFilter(x.filter));
    let pool = collapsePool;
    if (others.length) {
      pool = pool.filter((t) => others.every((oc) => {
        const v = getValue(t, oc.key);
        if (v == null) return false;
        // For DATE dropdowns we keep the dropdown values as the YYYY-MM-DD
        // prefix (see write path below). Slice the task value to the same
        // shape before comparing, otherwise "2025-08-11" never matches
        // "2025-08-11T00:00:00".
        const normVal = (val) => oc.type === "date" ? String(val).slice(0, 10) : String(val).toLowerCase();
        const filterValues = Array.isArray(oc.filter)
          ? oc.filter.map((x) => oc.type === "date" ? String(x).slice(0, 10) : String(x).toLowerCase())
          : [oc.type === "date" ? oc.filter.toString().slice(0, 10) : oc.filter.toString().trim().toLowerCase()];
        if (TASK_SEARCHABLE_KEYS.has(oc.key)) {
          const sv = String(v).toLowerCase();
          return filterValues.some((f) => sv.includes(f));
        }
        if (Array.isArray(v)) {
          return v.some((x) => filterValues.includes(normVal(x)));
        }
        return filterValues.includes(normVal(v));
      }));
    }

    const set = new Set();
    for (const t of pool) {
      const v = getValue(t, c.key);
      if (v == null || v === "") continue;
      if (Array.isArray(v)) v.forEach((x) => x != null && x !== "" && set.add(String(x)));
      else set.add(c.type === "date" ? String(v).slice(0, 10) : String(v));
    }
    out[c.key] = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }
  return out;
}

/**
 * Filter tasks by collapsed parents. A task is hidden when ANY of its
 * ancestors (via `parent_uid` chain) is in the collapsed set.
 */
function filterByCollapse(tasks, collapsed) {
  if (!collapsed || collapsed.size === 0) return tasks;
  const byUid = new Map(tasks.map((t) => [t.task_uid, t]));
  return tasks.filter((t) => {
    let p = t.parent_uid;
    while (p) {
      if (collapsed.has(p)) return false;
      const parent = byUid.get(p);
      p = parent?.parent_uid;
    }
    return true;
  });
}

export default function GanttChart({
  tasks = [],
  columns = [],
  period,
  zoom = "fit",
  showCriticalOnly = false,
  compareTasks = null,
  syncMeta = {},
  onTaskOpen,
  onColumnWidthChange,        // (key, newWidth) — optional; otherwise local state
  hSplit: hSplitProp,         // controlled split position (px). null/undef = derive from columns.
  onHSplitChange,             // (newSplit) — called when user drags the split handle
  collapsedUids,              // controlled list of collapsed group uids (string[])
  onCollapsedChange,          // (newList) — called when user toggles a group
  containerHeight = 560,
  emptyText,
}) {
  const { t } = useI18n();
  emptyText = emptyText ?? t("empty_no_tasks_version");
  const containerRef = useRef(null);
  const listOuterRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  // Local fallback when parent doesn't manage hSplit
  const [hSplitLocal, setHSplitLocal] = useState(540);
  const hSplit = hSplitProp != null ? hSplitProp : hSplitLocal;
  const setHSplit = (v) => { (onHSplitChange || setHSplitLocal)(v); };

  const [localWidths, setLocalWidths] = useState({}); // fallback if no onColumnWidthChange

  // Local fallback for collapsed state (uncontrolled)
  const [collapsedLocal, setCollapsedLocal] = useState([]);
  const collapsedList = collapsedUids != null ? collapsedUids : collapsedLocal;
  const collapsed = useMemo(() => new Set(collapsedList), [collapsedList]);

  // Resize observer on the outer container
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Parse dates once
  const parsed = useMemo(() => tasks.map((t) => ({
    ...t, _start: parseDate(t.start), _finish: parseDate(t.finish),
  })), [tasks]);

  const compareIdx = useMemo(() => {
    if (!compareTasks) return null;
    const m = new Map();
    compareTasks.forEach((t) => {
      m.set(`${t.project_id || ""}::${t.task_uid}`, { _start: parseDate(t.start), _finish: parseDate(t.finish) });
    });
    return m;
  }, [compareTasks]);

  // Distinct values per column for dropdown filters
  const distinctValues = useMemo(() => buildDistinctValues(parsed, columns, collapsed), [parsed, columns, collapsed]);

  // Apply filters: critical + period + per-column
  const filtered = useMemo(() => {
    let list = parsed;
    if (showCriticalOnly) list = list.filter((t) => t.is_critical);
    if (period?.start || period?.end) {
      const ps = period.start ? new Date(period.start + "T00:00:00") : null;
      const pe = period.end ? new Date(period.end + "T23:59:59") : null;
      list = list.filter((t) => {
        const ts = t._start, tf = t._finish;
        if (!ts && !tf) return true;
        const s = ts || tf, f = tf || ts;
        if (ps && f < ps) return false;
        if (pe && s > pe) return false;
        return true;
      });
    }
    // Filter is active when it has truthy content. We accept both legacy
    // string filters AND iter40 array filters (multi-select).
    const isActiveFilter = (f) => {
      if (Array.isArray(f)) return f.length > 0;
      return !!(f && f.toString().trim());
    };
    const activeFilters = columns.filter((c) => c.visible && isActiveFilter(c.filter));
    if (activeFilters.length) {
      list = list.filter((t) => activeFilters.every((c) => {
        const v = getValue(t, c.key);
        if (v == null) return false;
        // Date columns store full ISO timestamps but the dropdown options
        // are YYYY-MM-DD. Normalise both sides so the comparison succeeds.
        const normVal = (val) => c.type === "date" ? String(val).slice(0, 10) : String(val).toLowerCase();
        const filterValues = Array.isArray(c.filter)
          ? c.filter.map((x) => c.type === "date" ? String(x).slice(0, 10) : String(x).toLowerCase())
          : [c.type === "date" ? c.filter.toString().slice(0, 10) : c.filter.toString().trim().toLowerCase()];
        if (TASK_SEARCHABLE_KEYS.has(c.key)) {
          // Searchable text columns: substring match against ANY filter token.
          const sv = String(v).toLowerCase();
          return filterValues.some((f) => sv.includes(f));
        }
        // Dropdowns: ANY of the selected values matches exactly.
        if (Array.isArray(v)) {
          return v.some((x) => filterValues.includes(normVal(x)));
        }
        return filterValues.includes(normVal(v));
      }));
    }
    return filterByCollapse(list, collapsed);
  }, [parsed, showCriticalOnly, period, columns, collapsed]);

  // Compute timeline bounds + dayWidth
  const bounds = useMemo(() => {
    if (!filtered.length) return null;
    const starts = filtered.map((t) => t._start).filter(Boolean);
    const finishes = filtered.map((t) => t._finish).filter(Boolean);
    if (!starts.length || !finishes.length) return null;
    const min = new Date(Math.min(...starts.map((d) => d.getTime())));
    const max = new Date(Math.max(...finishes.map((d) => d.getTime())));
    const totalDays = Math.max(7, diffDays(max, min) + 1);
    const rightWidth = Math.max(200, containerWidth - hSplit - 1);
    let colWidth;
    if (zoom === "fit") {
      colWidth = Math.max(2, Math.floor(rightWidth / totalDays));
    } else {
      const preset = ZOOM_PRESETS[zoom] || ZOOM_PRESETS.fit;
      colWidth = Math.max(4, Math.floor(rightWidth / preset.days));
    }
    return { min, max, totalDays, colWidth, rightWidth, timelineWidth: totalDays * colWidth };
  }, [filtered, containerWidth, hSplit, zoom]);

  // Visible columns + resolved widths
  const visibleCols = useMemo(() => columns
    .filter((c) => c.visible)
    .map((c) => ({ ...c, width: localWidths[c.key] ?? c.width ?? 120 })),
  [columns, localWidths]);

  const totalLeftWidth = useMemo(
    () => visibleCols.reduce((s, c) => s + (c.width || 120), 0),
    [visibleCols],
  );

  // Keep hSplit in sync with column widths when columns change.
  // ONLY when uncontrolled — otherwise the parent owns hSplit and any nudge
  // here would fight with its persisted value.
  useEffect(() => {
    if (hSplitProp != null) return;
    if (totalLeftWidth && Math.abs(totalLeftWidth - hSplit) > 4) {
      setHSplitLocal(Math.min(Math.max(MIN_SPLIT, totalLeftWidth), Math.max(MIN_SPLIT, containerWidth * 0.7)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalLeftWidth, hSplitProp]);

  const setColWidth = useCallback((key, w) => {
    const clamped = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, Math.round(w)));
    if (onColumnWidthChange) onColumnWidthChange(key, clamped);
    else setLocalWidths((prev) => ({ ...prev, [key]: clamped }));
  }, [onColumnWidthChange]);

  // ---- Drag handlers ----------------------------------------------------
  const startColResize = (key, currentWidth) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const onMove = (ev) => {
      setColWidth(key, currentWidth + (ev.clientX - startX));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
  };

  const startSplitResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startSplit = hSplit;
    const onMove = (ev) => {
      const next = Math.min(Math.max(MIN_SPLIT, startSplit + (ev.clientX - startX)), containerWidth - 200);
      setHSplit(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
  };

  // ---- Collapse / expand ------------------------------------------------
  const toggleCollapse = useCallback((uid) => {
    const current = collapsedUids != null ? collapsedUids : collapsedLocal;
    const next = current.includes(uid) ? current.filter((x) => x !== uid) : [...current, uid];
    (onCollapsedChange || setCollapsedLocal)(next);
  }, [collapsedUids, collapsedLocal, onCollapsedChange]);

  // ---- Header markup (months + days) -----------------------------------
  const headerMarkup = useMemo(() => {
    if (!bounds) return null;
    const months = [];
    const cur = new Date(bounds.min.getFullYear(), bounds.min.getMonth(), 1);
    const end = bounds.max;
    while (cur <= end) {
      const ms = new Date(Math.max(cur.getTime(), bounds.min.getTime()));
      const me0 = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const me = me0 > end ? end : me0;
      months.push({
        key: `${cur.getFullYear()}-${cur.getMonth()}`,
        width: (diffDays(me, ms) + 1) * bounds.colWidth,
        label: cur.toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      });
      cur.setMonth(cur.getMonth() + 1);
      cur.setDate(1);
    }
    return { months };
  }, [bounds]);

  const totalWidth = (bounds?.timelineWidth || 0) + totalLeftWidth + 1;

  // ---- Row renderer ----------------------------------------------------
  const renderRow = useCallback(({ index, style }) => {
    const t = filtered[index];
    if (!t) return null;
    // Shift each row down by HEADER_HEIGHT so it sits BELOW the sticky header
    // (the header occupies the first HEADER_HEIGHT pixels of the inner element).
    const offsetStyle = {
      ...style,
      top: (parseFloat(style.top) || 0) + HEADER_HEIGHT,
      width: totalWidth,
    };
    const indent = Math.max(0, (t.outline_level || 1) - 1);
    const isCollapsed = collapsed.has(t.task_uid);
    // IMPORTANT: use SOLID bg colors here (no /40 opacity). The sticky-left
    // cells inherit this background and must completely hide bars rendered
    // in the timeline strip when horizontally scrolled — a translucent bg
    // would let the bars bleed through visually.
    const rowBg = index % 2 === 0 ? "bg-white" : "bg-zinc-50";
    return (
      <div
        style={offsetStyle}
        className={`flex border-b border-zinc-100 hover:bg-zinc-100 ${rowBg}`}
        onDoubleClick={() => onTaskOpen?.(t)}
        data-testid={`gantt-row-${t.task_uid}`}
      >
        <RowLeftCells
          task={t} cols={visibleCols} indent={indent}
          isCollapsed={isCollapsed} onToggleCollapse={toggleCollapse}
          totalLeftWidth={totalLeftWidth} rowBg={rowBg}
        />
        <RowTimeline task={t} bounds={bounds} compareIdx={compareIdx} syncMeta={syncMeta} />
      </div>
    );
  }, [filtered, visibleCols, totalLeftWidth, totalWidth, bounds, compareIdx, onTaskOpen, syncMeta, collapsed, toggleCollapse]);

  // ---- Sticky header (extracted to module scope to avoid recreating the
  // component type on every render — `react-window` remounts when the
  // `innerElementType` identity changes, which would destroy scroll position).
  // We bind the live props through a ref so the wrapper component is created
  // ONCE per `GanttChart` mount.
  const stickyProps = {
    totalWidth,
    totalLeftWidth,
    visibleCols,
    distinctValues,
    searchableKeys: TASK_SEARCHABLE_KEYS,
    bounds,
    headerMarkup,
    addDays,
    onColResize: startColResize,
    onSplitResize: startSplitResize,
  };
  const stickyPropsRef = useRef(stickyProps);
  stickyPropsRef.current = stickyProps;

  // `InnerType` is referentially stable for the lifetime of this chart.
  const InnerType = useMemo(
    () => forwardRef(function _Inner(props, ref) {
      return <GanttStickyHeader ref={ref} {...props} {...stickyPropsRef.current} />;
    }),
    [],
  );

  if (!filtered.length) {
    return (
      <div ref={containerRef} className="bg-white border border-zinc-200 rounded-md" data-testid="gantt-chart-empty">
        <div className="text-sm text-zinc-500 py-12 text-center" data-testid="gantt-empty">{emptyText}</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="gantt-chart">
      <List
        outerRef={listOuterRef}
        height={containerHeight}
        itemCount={filtered.length}
        itemSize={ROW_HEIGHT}
        width={containerWidth}
        overscanCount={10}
        innerElementType={InnerType}
        className="scrollbar-thin"
        style={{ overflowX: "auto" }}
      >
        {renderRow}
      </List>
      {/* Spacer so the list doesn't overlap the sticky header on the first paint */}
      <div className="px-4 py-2 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500 font-mono">
        <span data-testid="gantt-count">
          {filtered.length} {t("tasks_n")}
          {tasks.length !== filtered.length ? ` (${t("tasks_filtered_of")} ${tasks.length})` : ""}
        </span>
        {bounds && <span>zoom {zoom} · {bounds.colWidth}px/{t("day_short")} · {bounds.totalDays}d</span>}
      </div>
    </div>
  );
}

// ---- Row sub-components (memoised — only re-render when their props change) ---
const RowLeftCells = memo(function RowLeftCells({ task, cols, indent, isCollapsed, onToggleCollapse, totalLeftWidth, rowBg }) {
  return (
    <div
      // Sticky-left frozen pane. `z-10` puts us above the timeline bars; the
      // SOLID `rowBg` class (no opacity) means the bars never visually
      // bleed through when horizontally scrolled. The `border-r` gives a
      // clean visual seam between the columns and the timeline.
      className={`flex shrink-0 sticky left-0 z-10 border-r border-zinc-200 ${rowBg || "bg-white"}`}
      style={{ width: totalLeftWidth }}
    >
      {cols.map((col, ci) => {
        const isFirst = ci === 0;
        const w = col.width || 120;
        const val = getValue(task, col.key);
        const isNumeric = col.type === "number" || col.type === "percent";
        return (
          <div
            key={col.key}
            // `overflow-hidden` + the inner `truncate` span hard-clip any
            // content that would otherwise leak past the column width into
            // the timeline area. `min-w-0` lets flex shrink the cell when
            // the column width is reduced.
            className={`px-2 flex items-center text-xs shrink-0 overflow-hidden ${isNumeric ? "justify-end font-mono" : ""}`}
            style={{ width: w, paddingLeft: isFirst ? 4 + indent * 12 : 8 }}
            title={String(val ?? "")}
          >
            {isFirst && task.is_summary && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(task.task_uid); }}
                className="mr-1 text-zinc-400 hover:text-cs-accent text-[10px] cursor-pointer shrink-0 select-none w-3 inline-flex items-center justify-center"
                aria-label={isCollapsed ? "Expandir grupo" : "Recolher grupo"}
                data-testid={`gantt-toggle-${task.task_uid}`}
              >
                {isCollapsed ? "▶" : "▼"}
              </button>
            )}
            {isFirst && !task.is_summary && task.is_critical && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cs-accent mr-1.5 shrink-0" />
            )}
            <span className={`truncate min-w-0 ${isFirst && task.is_summary ? "font-bold text-zinc-900" : "text-zinc-700"}`}>
              {formatCell(val, col.type)}
            </span>
          </div>
        );
      })}
    </div>
  );
});

const RowTimeline = memo(function RowTimeline({ task, bounds, compareIdx, syncMeta }) {
  if (!bounds) return null;
  return (
    <div className="relative shrink-0" style={{ width: bounds.timelineWidth }}>
      {compareIdx && (() => {
        const k = `${task.project_id || ""}::${task.task_uid}`;
        const cmp = compareIdx.get(k);
        if (!cmp || !cmp._start || !cmp._finish) return null;
        const left = diffDays(cmp._start, bounds.min) * bounds.colWidth;
        const width = (diffDays(cmp._finish, cmp._start) + 1) * bounds.colWidth;
        return (
          <div
            className="absolute top-1.5 bottom-1.5 border border-dashed border-zinc-400 rounded-sm bg-zinc-100/60"
            style={{ left, width }}
            title={`Baseline: ${cmp._start.toLocaleDateString()} → ${cmp._finish.toLocaleDateString()}`}
          />
        );
      })()}
      {task._start && task._finish && (() => {
        const left = diffDays(task._start, bounds.min) * bounds.colWidth;
        const width = Math.max(2, (diffDays(task._finish, task._start) + 1) * bounds.colWidth);
        const pct = task.percent_complete || 0;
        const tint = task.__color || syncMeta[task.sync_id]?.color;
        const bg = task.is_summary
          ? "bg-zinc-700"
          : task.is_critical
            ? "bg-cs-accent border border-cs-accentHover"
            : "bg-cs-blueprint";
        return (
          <div
            className={`absolute top-1.5 bottom-1.5 rounded-sm shadow-sm flex items-center text-[10px] text-white font-mono px-1.5 ${bg}`}
            style={{ left, width, backgroundColor: tint || undefined }}
            data-testid={`gantt-bar-${task.task_uid}`}
            title={`${task.name}\n${task._start.toLocaleDateString()} → ${task._finish.toLocaleDateString()}\n${pct}% concluído`}
          >
            <div className="absolute inset-y-0 left-0 bg-zinc-950/30 rounded-sm" style={{ width: `${pct}%` }} />
            <span className="relative z-10 truncate">{task.task_id}</span>
          </div>
        );
      })()}
    </div>
  );
});

export { ZOOM_PRESETS, getValue, formatCell, parseDate, diffDays, addDays, buildDistinctValues, filterByCollapse };
